import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar as CalendarIcon, Clock, Users, BookOpen, Ticket, LogOut, Sparkles, CheckCircle2, Plus, Zap, Repeat, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";
import { format, parseISO } from "date-fns";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const memberHeaders = () => {
  const token = localStorage.getItem("member_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : null;
};

export default function MemberDashboardPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [classes, setClasses] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [filterSubject, setFilterSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [buying, setBuying] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  useEffect(() => {
    const auth = memberHeaders();
    if (!auth) { navigate("/member/login"); return; }
    const load = async () => {
      try {
        const [meRes, classRes, bookRes, subRes, pkgRes] = await Promise.all([
          axios.get(`${API}/member/me`, auth),
          axios.get(`${API}/classes?upcoming=true`),
          axios.get(`${API}/member/bookings`, auth),
          axios.get(`${API}/subjects`),
          axios.get(`${API}/member/credit-packages`),
        ]);
        setMe(meRes.data);
        setClasses(classRes.data);
        setMyBookings(bookRes.data);
        setSubjects(subRes.data);
        setPackages(pkgRes.data);

        // Resume purchase if user came from /pricing while logged out
        const intent = sessionStorage.getItem("pricing_intent");
        if (intent) {
          sessionStorage.removeItem("pricing_intent");
          setTopUpOpen(true);
        }
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("member_token");
          navigate("/member/login");
        } else {
          toast.error("Could not load dashboard");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const bookedClassIds = useMemo(
    () => new Set(myBookings.filter(b => b.status === "confirmed" || b.status === "paid").map(b => b.class_id)),
    [myBookings]
  );

  const filteredClasses = useMemo(() => {
    return classes.filter((c) => !filterSubject || c.subject_id === filterSubject);
  }, [classes, filterSubject]);

  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredClasses.forEach((c) => {
      if (!groups[c.date]) groups[c.date] = [];
      groups[c.date].push(c);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredClasses]);

  const handleBook = async (classId) => {
    if (!window.confirm("Use 1 credit to book this class for free?")) return;
    setBookingId(classId);
    try {
      const res = await axios.post(`${API}/member/book`, { class_id: classId }, memberHeaders());
      toast.success(`Booked! You have ${res.data.credits_remaining} credit${res.data.credits_remaining !== 1 ? "s" : ""} left this month.`);
      // refresh data
      const [meRes, classRes, bookRes] = await Promise.all([
        axios.get(`${API}/member/me`, memberHeaders()),
        axios.get(`${API}/classes?upcoming=true`),
        axios.get(`${API}/member/bookings`, memberHeaders()),
      ]);
      setMe(meRes.data); setClasses(classRes.data); setMyBookings(bookRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book");
    } finally {
      setBookingId(null);
    }
  };

  const logout = () => {
    localStorage.removeItem("member_token");
    localStorage.removeItem("member_name");
    navigate("/member/login");
  };

  const handleBuy = async (pkg) => {
    setBuying(pkg.id);
    try {
      if (pkg.id === "weekly") {
        trackEvent("begin_checkout", { content_type: "subscription", content_ids: ["weekly_subscription"], value: pkg.price, currency: "AUD" });
        const res = await axios.post(
          `${API}/member/checkout/subscribe-weekly`,
          { origin_url: window.location.origin },
          memberHeaders()
        );
        window.location.href = res.data.url;
        return;
      }
      trackEvent("begin_checkout", {
        value: pkg.price,
        currency: "AUD",
        content_type: "credit_pack",
        content_ids: [pkg.id],
        num_items: pkg.credits,
      });
      const res = await axios.post(
        `${API}/member/checkout/credits`,
        { package_id: pkg.id, origin_url: window.location.origin },
        memberHeaders()
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBuying(null);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API}/member/change-password`,
        { current_password: pwForm.current_password, new_password: pwForm.new_password },
        memberHeaders()
      );
      toast.success("Password updated");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setPwSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Cancel your weekly subscription? Your existing credits will still work, but no new credits will be added.")) return;
    try {
      await axios.post(`${API}/member/subscription/cancel`, {}, memberHeaders());
      toast.success("Subscription cancelled. It will not renew next week.");
      const meRes = await axios.get(`${API}/member/me`, memberHeaders());
      setMe(meRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    }
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0f1117]">
        <Navbar />
        <div className="pt-32 text-center text-slate-500 font-body">Loading your dashboard...</div>
      </div>
    );
  }

  const firstName = me.name.split(" ")[0];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="member-dashboard-page">
      <Seo title="Member dashboard" description="Manage your credits and book classes." noindex />
      <Navbar />

      {/* Header / Welcome */}
      <section className="hero-gradient dark:bg-transparent pt-28 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 animate-fade-in-up">
            <div>
              <Badge className="bg-brand-purple-soft text-brand-purple mb-3 font-body font-semibold px-4 py-1.5 rounded-full">
                <Sparkles className="inline h-3.5 w-3.5 mr-1" /> Member Portal
              </Badge>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">
                Welcome, <span className="text-brand-purple dark:text-brand-purple-light">{firstName}</span> 👋
              </h1>
              <p className="text-slate-600 dark:text-slate-400 font-body mt-2">Top up credits and book any upcoming class — it only takes a minute.</p>
            </div>
            <Button variant="outline" onClick={logout}
              className="rounded-full font-body border-brand-purple text-brand-purple hover:bg-brand-purple-soft self-start md:self-center"
              data-testid="member-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            <Button variant="outline" size="sm" onClick={() => setPwOpen(true)}
              className="rounded-full font-body text-xs border-slate-300 dark:border-slate-700 hover:border-brand-purple hover:text-brand-purple"
              data-testid="change-password-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Change password
            </Button>
          </div>

          {/* Credit cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-brand-gold to-brand-gold-hover rounded-2xl p-6 text-white shadow-lg" data-testid="credits-card">
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="h-5 w-5" />
                <span className="font-body text-xs uppercase tracking-wider">Credits remaining</span>
              </div>
              <p className="font-heading text-5xl font-bold mb-1">{me.credits_remaining}</p>
              <p className="text-xs font-body opacity-90 mb-3">1 credit = 1 class</p>
              <Button
                size="sm"
                onClick={() => setTopUpOpen(true)}
                className="bg-white/20 hover:bg-white/30 text-white border border-white/40 rounded-full font-body text-xs font-semibold backdrop-blur-sm"
                data-testid="top-up-credits-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Top up credits
              </Button>
            </div>
            <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6" data-testid="month-card">
              <div className="flex items-center gap-2 mb-2">
                <CalendarIcon className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
                <span className="font-body text-xs uppercase tracking-wider text-slate-500">Credits expire in</span>
              </div>
              <p className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-1">
                {me.credits_remaining > 0 ? `${me.credits_expiry_days} days` : "—"}
              </p>
              <p className="text-xs font-body text-slate-500">
                {me.credits_remaining > 0
                  ? `if no credits are used for ${me.credits_expiry_weeks} weeks straight`
                  : "Buy credits to start booking"}
              </p>
            </div>
            <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6" data-testid="booked-card">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-body text-xs uppercase tracking-wider text-slate-500">Booked this month</span>
              </div>
              <p className="font-heading text-3xl font-bold text-slate-900 dark:text-white mb-1">{bookedClassIds.size}</p>
              <p className="text-xs font-body text-slate-500">classes confirmed</p>
            </div>
          </div>

          {me.subscription?.active && (
            <div className="mt-4 bg-gradient-to-br from-brand-purple/10 to-brand-purple/5 dark:from-brand-purple/20 dark:to-brand-purple/10 border border-brand-purple/30 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="subscription-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-purple/15 flex items-center justify-center">
                  <Repeat className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
                </div>
                <div>
                  <p className="font-heading font-bold text-slate-900 dark:text-white text-sm">Weekly subscription active</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-body">
                    {me.subscription.cancel_at_period_end
                      ? "Ends at the close of this billing week — no further credits will be added."
                      : `Card charged $88/week ($80 + GST). +1 ${me.subscription.subject_name || ""} credit added on every successful payment.`}
                  </p>
                </div>
              </div>
              {!me.subscription.cancel_at_period_end && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelSubscription}
                  className="rounded-full font-body text-xs border-slate-300 dark:border-slate-700 hover:border-red-400 hover:text-red-600"
                  data-testid="cancel-subscription-btn"
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Upcoming classes */}
      <section className="py-10 px-6 bg-white dark:bg-[#0f1117]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">Upcoming classes</h2>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={!filterSubject ? "default" : "outline"} onClick={() => setFilterSubject("")}
                className={`rounded-full font-body text-xs ${!filterSubject ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : ""}`}
                data-testid="member-filter-all">All</Button>
              {subjects.map((s) => (
                <Button key={s.id} size="sm" variant={filterSubject === s.id ? "default" : "outline"} onClick={() => setFilterSubject(s.id)}
                  className={`rounded-full font-body text-xs ${filterSubject === s.id ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : ""}`}
                  data-testid={`member-filter-${s.id}`}>{s.name}</Button>
              ))}
            </div>
          </div>

          {me.credits_remaining === 0 && (
            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4 mb-6 text-sm font-body text-amber-900 dark:text-amber-300 flex flex-wrap items-center justify-between gap-3" data-testid="no-credits-warning">
              <span>You have no credits left. Top up in seconds — pay by card, credits hit your account instantly.</span>
              <Button
                size="sm"
                onClick={() => setTopUpOpen(true)}
                className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full font-body text-xs font-semibold"
                data-testid="no-credits-topup-btn"
              >
                <Zap className="h-3.5 w-3.5 mr-1" /> Top up now
              </Button>
            </div>
          )}
          {me.credits_remaining > 0 && me.credits_expiry_days <= 14 && (
            <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-4 mb-6 text-sm font-body text-blue-900 dark:text-blue-300" data-testid="expiry-warning">
              ⏰ Your credits expire in <strong>{me.credits_expiry_days} days</strong> if not used. Book a class to refresh the timer.
            </div>
          )}

          {filteredClasses.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 dark:bg-[#1a1b23] rounded-2xl" data-testid="no-classes-state">
              <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-body">No upcoming classes match this filter.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groupedByDate.map(([date, dayClasses]) => (
                <div key={date} data-testid={`member-date-group-${date}`}>
                  <h3 className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light mb-3">
                    {format(parseISO(date), "EEEE, MMM d, yyyy")}
                  </h3>
                  <div className="space-y-3">
                    {dayClasses.map((cls) => {
                      const spotsLeft = cls.max_occupancy - cls.current_occupancy;
                      const isFull = spotsLeft <= 0;
                      const alreadyBooked = bookedClassIds.has(cls.id);
                      return (
                        <div key={cls.id} className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-5 card-hover transition-colors" data-testid={`member-class-${cls.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge className="bg-brand-purple-soft dark:bg-brand-purple/20 text-brand-purple dark:text-brand-purple-light font-body text-xs rounded-full">{cls.subject_name}</Badge>
                                {alreadyBooked && <Badge className="bg-green-100 text-green-700 font-body text-xs rounded-full">Booked</Badge>}
                              </div>
                              <h4 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">{cls.title}</h4>
                              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400 font-body mt-1">
                                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{cls.start_time} - {cls.end_time}</span>
                                <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />
                                  <span className={isFull ? "text-red-500 font-semibold" : spotsLeft <= 3 ? "text-amber-600 font-semibold" : ""}>
                                    {isFull ? "Full" : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                                  </span>
                                </span>
                                <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />{cls.tutor_name}</span>
                              </div>
                            </div>
                            <Button
                              onClick={() => {
                                if (me.credits_remaining > 0 && !alreadyBooked && !isFull) {
                                  handleBook(cls.id);
                                } else if (!alreadyBooked && !isFull && me.credits_remaining <= 0) {
                                  navigate(`/book/${cls.id}`);
                                }
                              }}
                              disabled={isFull || alreadyBooked || bookingId === cls.id}
                              className={`rounded-full px-6 font-body font-semibold transition-all ${
                                alreadyBooked
                                  ? "bg-green-100 text-green-700"
                                  : isFull
                                  ? "bg-slate-200 text-slate-400"
                                  : me.credits_remaining <= 0
                                  ? "bg-brand-purple hover:bg-brand-purple-hover text-white shadow-md hover:shadow-lg"
                                  : "bg-brand-gold hover:bg-brand-gold-hover text-white shadow-md hover:shadow-lg"
                              }`}
                              data-testid={`member-book-btn-${cls.id}`}
                            >
                              {bookingId === cls.id ? "Booking..." :
                                alreadyBooked ? "Booked" :
                                isFull ? "Full" :
                                me.credits_remaining <= 0 ? `Pay $${(cls.price * 1.1).toFixed(2)}` :
                                "Use 1 credit"}                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* My bookings */}
      {myBookings.length > 0 && (
        <section className="py-10 px-6 bg-slate-50 dark:bg-[#0a0b10]" data-testid="my-bookings-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-4">My booking history</h2>
            <div className="space-y-2">
              {myBookings.slice(0, 10).map((b) => (
                <div key={b.id} className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-4 flex items-center justify-between gap-3" data-testid={`my-booking-${b.id}`}>
                  <div>
                    <p className="font-body font-semibold text-slate-900 dark:text-white text-sm">{b.class_title}</p>
                    <p className="text-xs text-slate-500 font-body">{b.class_date} · {b.class_time} · {b.subject_name}</p>
                  </div>
                  <Badge className={`font-body text-xs rounded-full ${b.payment_status === "free_credit" ? "bg-brand-gold/10 text-brand-gold" : "bg-green-100 text-green-700"}`}>
                    {b.payment_status === "free_credit" ? "FREE CREDIT" : (b.status || "confirmed").toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="bg-white dark:bg-[#0f1117] border-t border-slate-100 dark:border-slate-800 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs text-slate-400 dark:text-slate-500 font-body">&copy; {new Date().getFullYear()} Tutors Melbourne — Member Portal</p>
        </div>
      </footer>

      {/* Top up credits modal */}
      <Dialog open={topUpOpen} onOpenChange={setTopUpOpen}>
        <DialogContent className="max-w-2xl bg-white dark:bg-[#1a1b23] border-slate-200 dark:border-slate-800" data-testid="top-up-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-slate-900 dark:text-white flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand-gold" /> Top up credits
            </DialogTitle>
            <DialogDescription className="font-body text-slate-500 dark:text-slate-400">
              Pay securely by card. Credits land in your account the moment payment clears.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {packages.map((p) => {
              const isSub = p.id === "weekly";
              const perCredit = (p.price / p.credits).toFixed(2);
              const highlight = p.id === "term-10";
              const alreadySubscribed = isSub && me.subscription?.active;
              return (
                <div
                  key={p.id}
                  className={`rounded-2xl border-2 p-5 flex flex-col transition-all ${
                    highlight
                      ? "border-brand-gold bg-brand-gold/5"
                      : isSub
                      ? "border-brand-purple/40 bg-brand-purple/5"
                      : "border-slate-200 dark:border-slate-700 hover:border-brand-purple/50"
                  }`}
                  data-testid={`topup-package-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-heading text-base font-bold text-slate-900 dark:text-white">{isSub ? "Weekly Plan" : p.label}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-body">
                        {isSub ? "Card charged $88/week ($80 + GST) — +1 credit every week. Cancel anytime." : `${p.credits} credit${p.credits > 1 ? "s" : ""} · $${perCredit} per class + GST`}
                      </p>
                    </div>
                    {highlight && <Badge className="bg-brand-gold text-white font-body text-[10px] rounded-full">POPULAR</Badge>}
                    {isSub && <Badge className="bg-brand-purple text-white font-body text-[10px] rounded-full">SUBSCRIPTION</Badge>}
                  </div>
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-3xl font-bold text-slate-900 dark:text-white">${p.price.toFixed(0)}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-body font-semibold">+ GST</span>
                      <span className="text-xs text-slate-400 font-body">{isSub ? "AUD / week" : "AUD"}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-body mt-0.5" data-testid={`topup-gst-${p.id}`}>
                      ${(p.total_inc_gst ?? p.price * 1.1).toFixed(2)} charged incl. GST{isSub ? " each week" : ""}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      if (alreadySubscribed) return;
                      handleBuy(p);
                    }}
                    disabled={buying !== null || alreadySubscribed}
                    className={`mt-auto rounded-full font-body font-semibold py-5 ${
                      highlight
                        ? "bg-brand-gold hover:bg-brand-gold-hover text-white"
                        : "bg-brand-purple hover:bg-brand-purple-hover text-white"
                    }`}
                    data-testid={`topup-buy-${p.id}`}
                  >
                    {alreadySubscribed
                      ? "Already subscribed"
                      : buying === p.id
                      ? "Redirecting…"
                      : isSub
                      ? "Start $80/week + GST subscription"
                      : `Pay $${p.price.toFixed(0)}`}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 font-body mt-3 text-center">
            Payments via Stripe (AUD). Prices exclude GST — 10% is added at checkout. Credits expire after 10 weeks of no usage.
            <Link to="/pricing" className="text-brand-purple hover:underline ml-1">See full pricing →</Link>
          </p>
        </DialogContent>
      </Dialog>

      {/* Change password */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-[#1a1b23]" data-testid="change-password-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900 dark:text-white flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-brand-purple" /> Change your password
            </DialogTitle>
            <DialogDescription className="font-body text-slate-500 dark:text-slate-400">
              Enter your current password, then choose a new one (at least 6 characters).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="pw-current" className="font-body text-sm">Current password</Label>
              <Input id="pw-current" type="password" value={pwForm.current_password} required
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="mt-1.5 rounded-xl" data-testid="pw-current-input" />
            </div>
            <div>
              <Label htmlFor="pw-new" className="font-body text-sm">New password</Label>
              <Input id="pw-new" type="password" value={pwForm.new_password} required minLength={6}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="mt-1.5 rounded-xl" data-testid="pw-new-input" />
            </div>
            <div>
              <Label htmlFor="pw-confirm" className="font-body text-sm">Confirm new password</Label>
              <Input id="pw-confirm" type="password" value={pwForm.confirm} required minLength={6}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="mt-1.5 rounded-xl" data-testid="pw-confirm-input" />
            </div>
            <Button type="submit" disabled={pwSaving}
              className="w-full rounded-full bg-brand-purple hover:bg-brand-purple-hover text-white font-body font-bold py-5"
              data-testid="pw-submit-btn">
              {pwSaving ? "Saving…" : "Update password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
